import api from './api'

export interface JournalEntryLine {
  posting_type: 'Debit' | 'Credit'
  amount: number | string
  account_id: string
  account_name: string
  description?: string
  department_id?: string | null
  department_name?: string | null
  entity_id?: string | null
  entity_name?: string | null
  entity_type?: string | null
}

export interface JournalEntryDraft {
  id: number
  tool: string
  status: string
  txn_date: string
  doc_number: string
  lines: JournalEntryLine[]
  total_debits: number | string
  total_credits: number | string
  warnings?: string[] | null
  source_data?: Record<string, unknown> | null
  private_note?: string | null
  qbo_entity_id?: string | null
  qbo_url?: string | null
  error_message?: string | null
  created_at: string
  posted_at?: string | null
}

export interface JournalEntryAttachment {
  filename: string
  content_type: string
  content_base64: string
}

export interface JournalEntryPostResponse {
  draft: JournalEntryDraft
  attachment_warnings: string[]
}

export interface QboIdMappingItem {
  key: string
  qbo_id: string
  display_name: string
  mapping_type: string
}

export interface QboIdMappingsResponse {
  mappings: QboIdMappingItem[]
  total: number
}

export interface ExternalCredentialResponse {
  service: string
  configured: boolean
  keys: string[]
  updated_at?: string | null
  last_verified_at?: string | null
}

export async function getJournalEntryDraft(draftId: number): Promise<JournalEntryDraft> {
  const response = await api.get<JournalEntryDraft>(`/accounting/journal-entries/${draftId}`)
  return response.data
}

export async function updateJournalEntryLines(
  draftId: number,
  lines: JournalEntryLine[]
): Promise<JournalEntryDraft> {
  const response = await api.put<JournalEntryDraft>(
    `/accounting/journal-entries/${draftId}/lines`,
    { lines }
  )
  return response.data
}

export async function postJournalEntry(
  draftId: number,
  attachments: JournalEntryAttachment[] = []
): Promise<JournalEntryPostResponse> {
  const response = await api.post<JournalEntryPostResponse>(
    `/accounting/journal-entries/${draftId}/post`,
    { attachments }
  )
  return response.data
}

export async function getQboIdMappings(): Promise<QboIdMappingsResponse> {
  const response = await api.get<QboIdMappingsResponse>('/accounting/qbo-id-mappings')
  return response.data
}

export async function putQboIdMappings(
  mappings: QboIdMappingItem[]
): Promise<QboIdMappingsResponse> {
  const response = await api.put<QboIdMappingsResponse>('/accounting/qbo-id-mappings', {
    mappings,
  })
  return response.data
}

export async function getExternalCredentials(
  service: string
): Promise<ExternalCredentialResponse> {
  const response = await api.get<ExternalCredentialResponse>(
    `/accounting/external-credentials/${service}`
  )
  return response.data
}

export async function putExternalCredentials(
  service: string,
  values: Record<string, unknown>
): Promise<ExternalCredentialResponse> {
  const response = await api.put<ExternalCredentialResponse>(
    `/accounting/external-credentials/${service}`,
    { values }
  )
  return response.data
}
